import { Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";
import { MessageType } from "src/api/types";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { parseMessage } from "src/pages/Cancellations/utils/parseMessage";

interface Props {
  message: MessageType;
}

type Properties = {
  showName: boolean;
  backgroundColor: string;
  position: React.CSSProperties["alignItems"];
  color: string;
  textColor: string;
  textAlign: React.CSSProperties["textAlign"];
};
const Message = ({ message }: Props) => {
  const profileId = useClinician((clinician) => clinician.id);

  const properties: Properties = useMemo(() => {
    if (message.sender.id === profileId) {
      return {
        showName: false,
        backgroundColor: "#00B6B9",
        position: "flex-end",
        color: "#91E7E8",
        textColor: "#fff",
        textAlign: "right",
      };
    }

    return {
      showName: true,
      backgroundColor: "#CCF6F7",
      color: "#82BEC0",
      position: "flex-start",
      textColor: "#000",
      textAlign: "left",
    };
  }, [message.sender.id, profileId]);

  const name = useMemo(() => {
    if (message.sender.preferred_name) {
      return message.sender.preferred_name;
    }
    return message.sender.first_name + " " + message.sender.last_name;
  }, [
    message.sender.first_name,
    message.sender.last_name,
    message.sender.preferred_name,
  ]);

  return (
    <Stack role="listitem" sx={{ whiteSpace: "pre-wrap" }}>
      {properties.showName ? (
        <Typography color={properties.color} ml={1} variant="body2">
          {name}
        </Typography>
      ) : null}
      <Stack
        alignSelf={properties.position}
        p={2}
        gap={0.5}
        sx={{
          backgroundColor: properties.backgroundColor,
          borderRadius: "10px",
          minWidth: "10%",
          maxWidth: "60%",
          mb: 1,
          wordWrap: "break-word",
        }}
      >
        <Typography
          role="article"
          aria-label="Message text"
          color={properties.textColor}
          textAlign={properties.textAlign}
        >
          {parseMessage(message.message)}
        </Typography>
        <Typography
          color={properties.color}
          textAlign={properties.textAlign}
          fontSize="0.7rem"
        >
          {format(new Date(message.created_at), `MMM d yyyy 'at' h:mm a`)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default Message;
